import { useState } from "react";
import LayoutPageView from "./LayoutPageView";
import useZoomPanEditor from "../../../components/zoomPanEditor/useZoomPanEditor";
import RandomizeHelper from "../../../helpers/RandomizeHelper";
import KeyboardHelper from "../../../helpers/KeyboardHelper";
import { getDefaultLayoutSettings } from "../../../components/layout/LayoutDefaultSettings";
import useCustomEffect from "../../../hooks/useCustomEffect";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";
import {
  layoutFromApi,
  removeFromLayout,
} from "../../../components/layout/LayoutMethods";
import { useParams } from "react-router-dom";
import { useAuthContext } from "../../../context/AuthContext";

function LayoutPage() {
  const callEvaApi = useEvaApi();
  const authContext = useAuthContext();
  const zoomPanController = useZoomPanEditor();
  const [images, setImages] = useState([]);
  const [bcCompanies, setBcCompanies] = useState([]);
  const { layout } = useParams();
  const [layoutData, setLayoutData] = useState({
    name: "",
    focused_item_id: null,
    hovered_item_id: null,
    layout: {
      id: RandomizeHelper.getUUID(),
      type: "page",
      style: getDefaultLayoutSettings("page").style,
      children: [],
    },
  });

  /**
   * UseEffect for initializations and cleanup
   */
  useCustomEffect(() => {
    fetchImages();

    if (authContext.auth.user.company.type === "business_center") {
      fetchBcCompanies();
    }

    if (layout) {
      fetchLayoutData();
    }

    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keyup", onKeyUp);
    };
  });

  /**
   * This method will fetch the layout data from the API
   */
  async function fetchLayoutData() {
    await callEvaApi(
      new EvaApiCall(`layouts/${layout}`)
        .setLoadingGroup("layout_page_fetch")
        .setParams({
          with_layout_layout_data: true,
          with_layout_item_image_data: true,
          with_layout_item_children_data: true,
          with_layout_item_business_center_company_data: true,
          with_business_center_company_company_data: true,
          with_business_center_company_logo_data: true,
        })
        .setOnSuccess((response) => {
          setLayoutData((prev) => ({
            ...prev,
            name: response.data.data.name,
            layout: layoutFromApi(response.data.data.layout),
          }));
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the images from the API
   */
  async function fetchImages() {
    await callEvaApi(
      new EvaApiCall("files")
        .setLoadingGroup("layout_page_fetch")
        .setParams({
          where: [{ column: "type", values: ["image"] }],
        })
        .setOnSuccess((response) => {
          setImages(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the business center companies from the API
   */
  async function fetchBcCompanies() {
    await callEvaApi(
      new EvaApiCall("business_center_companies")
        .setLoadingGroup("layout_page_fetch")
        .setParams({
          with_business_center_company_company_data: true,
          with_business_center_company_logo_data: true,
        })
        .setOnSuccess((response) => {
          setBcCompanies(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will handle the window key up event
   * @param {Event} e
   */
  function onKeyUp(e) {
    //Don't delete items while the user is typing in an input
    if (KeyboardHelper.isEditableTarget(e.target)) return;

    if (e.key === "Delete") {
      setLayoutData((prev) => ({
        ...prev,
        layout:
          prev.focused_item_id !== prev.layout.id
            ? removeFromLayout(prev.layout, prev.focused_item_id)
            : prev.layout,
        focused_item_id:
          prev.focused_item_id !== prev.layout.id ? null : prev.focused_item_id,
      }));
    }
  }

  return (
    <LayoutPageView
      layout={layout}
      layoutData={layoutData}
      setLayoutData={setLayoutData}
      zoomPanController={zoomPanController}
      images={images}
      bcCompanies={bcCompanies}
    />
  );
}

export default LayoutPage;
